import { Button, Form, Input, Modal } from "antd";
import { useWatch } from "antd/es/form/Form";
import TextArea from "antd/es/input/TextArea";
import { saveAs } from "file-saver";
import PhotoSwipeDynamicCaption from "photoswipe-dynamic-caption-plugin";
import PhotoSwipeLightbox from "photoswipe/lightbox";
import "photoswipe/style.css";
import { useEffect, useMemo, useState } from "react";
import { Save } from "react-feather";
import { useTranslation } from "react-i18next";
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";
// import Masonry from 'react-masonry-css'

import {
  useCommentApi,
  useLikedApi,
  useRecommendApi,
  useTagApi,
} from "../../services/apis/file.api";
import { TAlbumDetail, TImage } from "../../types/album.type";
import MessageModal from "../MessageModal";
import {
  addEventTag,
  getRecommendedUserButton,
  getTagDeleteButton,
  getTagModal,
} from "./PhotoActions";

import { ACTION_ALBUM, PATH_ALBUM } from "../../constants/album";
import { isIOS } from "../../utils/common";
import { useChangeThumbnailApi } from "../../services/apis/album.api";
import Card from "./Card";
import useColumnCount from "../../hook/useColumnCount";

declare global {
  interface Window {
    pswp: any;
    passComment: string;
    lightbox: PhotoSwipeLightbox;
    clickImage: any;
    fetchNextPage: (options?: any) => Promise<any>;
    isFetchingNextPage: boolean;
  }
}
const AlbumGallery = ({
  images,
  albumDetail,
  fetchNextPage,
  checkedPath,
  isCommentPassword,
  isDownloadable,
  maxFileLikes,
}: {
  images: TImage[];
  albumDetail: TAlbumDetail;
  fetchNextPage: (options?: any) => Promise<any>;
  checkedPath: string;
  isCommentPassword?: boolean;
  isDownloadable?: boolean;
  maxFileLikes?: number;
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { mutate: mutateRecommend } = useRecommendApi();
  const { mutate: mutateLiked } = useLikedApi();
  const { mutate: mutateComment, isLoading: loadingComment } = useCommentApi();
  const { mutate: mutateTag } = useTagApi();
  const { mutate: mutateChangeThumbnail } = useChangeThumbnailApi();
  const [errorPassComment, setErrorPassComment] = useState<string>("");
  const [passComment, setPassComment] = useState<string>(null);
  const comment = useWatch("comment", form);
  const commentPass = useWatch("comment_password", form);
  const [isModalOpen, setIsModalOpen] = useState<ACTION_ALBUM>(null);
  const columnsCountBreakPoints = {
    1024: 4,
    768: 3,
    375: 2,
  };
  const columnCount = useColumnCount(columnsCountBreakPoints);
  const [isCommentModalOpen, setIsCommentModalOpen] = useState<{
    id: string;
  }>(null);
  const [isError, setIsError] = useState<"like" | "change-thumbnail">(undefined);
  const [aspectRatios, setAspectRatios] = useState({});
  const [tagShow, setTagShow] = useState<string[]>([]);

  // Function để tính tổng aspectRatios của mỗi column
  const calculateColumnAspectRatios = useMemo(() => {
    console.log('images.length', images.length);
    console.log('columnCount', columnCount);
    console.log('albumDetail.files.meta.total', albumDetail.files.meta.total);
    if (!images.length || !columnCount || images.length !== albumDetail.files.meta.total) return {};

    // Khởi tạo mảng để lưu tổng aspectRatios của mỗi column
    const columnTotals = new Array(columnCount).fill(0);
    const columnItems = new Array(columnCount).fill(null).map(() => []);

    // Phân bổ ảnh vào các column dựa trên thuật toán masonry
    images.forEach((image, index) => {
      if ((image as any).is_empty) return; // Bỏ qua empty items

      const aspectRatio = aspectRatios[index];
      if (!aspectRatio) return; // Bỏ qua ảnh chưa load

      // Tìm column có tổng aspectRatios nhỏ nhất
      const minColumnIndex = columnTotals.indexOf(Math.min(...columnTotals));

      // Thêm aspectRatio vào column đó
      columnTotals[minColumnIndex] += aspectRatio;
      columnItems[minColumnIndex].push({
        index,
        aspectRatio,
        image
      });
    });

    return {
      columnTotals,
      columnItems,
      totalImages: images.filter(img => !(img as any).is_empty).length
    };
  }, [images, aspectRatios, columnCount,albumDetail.files.meta.total]);

  // Kiểm tra xem tất cả ảnh đã load xong chưa
  const allImagesLoaded = useMemo(() => {
    const totalImages = images.filter(img => !(img as any).is_empty).length;
    const loadedImages = Object.keys(aspectRatios).length;
    return totalImages > 0 && loadedImages === totalImages;
  }, [images, aspectRatios]);

  // Function để sắp xếp lại images theo thứ tự column xen kẽ
  const reorderedImages = useMemo(() => {
    if (!allImagesLoaded || !calculateColumnAspectRatios.columnItems ||
        !calculateColumnAspectRatios.columnItems?.length) {
      return images;
    }


    const { columnItems } = calculateColumnAspectRatios;
    const reordered = [];
    const maxLength = Math.max(...columnItems.map(col => col.length));

    // Lặp qua từng vị trí (0, 1, 2, ...)
    for (let position = 0; position < maxLength; position++) {
      // Lặp qua từng column
      for (let columnIndex = 0; columnIndex < columnItems.length; columnIndex++) {
        const column = columnItems[columnIndex];

        if (position < column.length) {
          // Có item ở vị trí này
          const item = column[position];
          reordered.push(item.image);
        } else {
          // Không có item, thêm empty item
          reordered.push({
            id: `empty-${columnIndex}-${position}`,
            is_empty: true,
            url: '',
            file_name: '',
            folder_id: 0,
            gg_file_id: '',
            created_at: '',
            updated_at: '',
            is_liked: 0,
            is_recommended: 0,
            comments_count: 0,
            comments: [],
            tags: []
          });
        }
      }
    }

    return reordered;
  }, [calculateColumnAspectRatios, images, allImagesLoaded]);

  // Debug: Log kết quả tính toán
  // useEffect(() => {
  //   if (calculateColumnAspectRatios.columnTotals) {
  //     console.log('=== DEBUG REORDERING ===');
  //     console.log('All Images Loaded:', allImagesLoaded);
  //     console.log('Column Aspect Ratios Totals:', calculateColumnAspectRatios.columnTotals);
  //     console.log('Column Items:', calculateColumnAspectRatios.columnItems);
  //     console.log('Total Images:', calculateColumnAspectRatios.totalImages);
  //     console.log('Using reordered images:', reorderedImages !== images);
  //     console.log('Reordered Images count:', reorderedImages.length);
  //     console.log('Original Images count:', images.length);

  //     // Log chi tiết từng column
  //     calculateColumnAspectRatios.columnTotals.forEach((total, index) => {
  //       console.log(`Column ${index + 1}: Total = ${total.toFixed(2)}, Items = ${calculateColumnAspectRatios.columnItems[index].length}`);
  //     });

  //     // Log thứ tự sắp xếp
  //     if (reorderedImages !== images) {
  //       console.log('Reordered Images IDs:', reorderedImages.map(img => img.id));
  //     }
  //     console.log('========================');
  //   }
  // }, [calculateColumnAspectRatios, allImagesLoaded, reorderedImages, images]);

  // Expose column info to window for debugging/access
  useEffect(() => {
    if (calculateColumnAspectRatios.columnTotals) {
      (window as any).getColumnAspectRatios = () => calculateColumnAspectRatios;
    }
  }, [calculateColumnAspectRatios]);

  const handleImageLoad = (index: number, event) => {
    // Nếu đang sử dụng fallback (reorderedImages === images), sử dụng index trực tiếp
    if (reorderedImages === images) {
      if (aspectRatios[index]) return;
      const img = event.target as HTMLImageElement;
      const aspectRatio = img.naturalHeight / img.naturalWidth;
      setAspectRatios((prev) => ({
        ...prev,
        [index]: aspectRatio,
      }));
      return;
    }

    // Tìm original index của image trong mảng images ban đầu
    const image = reorderedImages[index];
    if (!image || (image as any).is_empty) return;

    const originalIndex = images.findIndex(img => img.id === image.id);
    if (originalIndex === -1) return;

    if (aspectRatios[originalIndex]) return;
    const img = event.target as HTMLImageElement;
    const aspectRatio = img.naturalHeight / img.naturalWidth;
    setAspectRatios((prev) => ({
      ...prev,
      [originalIndex]: aspectRatio,
    }));
  };

  const onFinish = (values: any) => {
    // Tìm image bằng fileId
    const targetImage = images.find(img => img.id === isCommentModalOpen.id);
    if (!targetImage) return;

    mutateComment(
      {
        id: targetImage.id,
        dataQuery: {
          comment: values?.comment,
          comment_password: values?.comment_password,
        },
      },
      {
        onSuccess: () => {
          document
            .querySelectorAll("#comment-" + targetImage.id)
            .forEach((element: any) => {
              element.innerHTML = values?.comment;
              const closestCommentWrap = (element as HTMLElement).closest(
                ".comment-wrap"
              );
              if (closestCommentWrap && !!values?.comment) {
                (closestCommentWrap as HTMLElement).style.backgroundColor =
                  "#414141";
              }
            });

          targetImage.comments = [
            {
              ...targetImage.comments[0],
              comment: values?.comment,
            },
          ];
          if (passComment !== values.comment_password) {
            setPassComment(values.comment_password);
            window.passComment = values.comment_password;
          }
          setIsCommentModalOpen(null);
        },
        onError: () => {
          setErrorPassComment(t("formConfimPassAlbum.confirmFail"));
        },
      }
    );
  };

  useEffect(() => {
    if (errorPassComment !== "") setErrorPassComment("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comment, commentPass]);

  useEffect(() => {
    window.fetchNextPage = fetchNextPage;
  }, [fetchNextPage]);

  const recommendF = (fileId, e) => {
    const dom = document.getElementById("recommend-" + fileId);
    dom.click();
  };
  const likeF = (fileId, e) => {
    const dom = Array.from(
      document.querySelectorAll("#like-" + fileId)
    ).pop() as HTMLElement;
    dom.click();
  };

  const commentF = (fileId) => {
    const comment = document.getElementById("comment-" + fileId)?.textContent;
    form.setFieldValue("comment", comment || "");

    form.setFieldValue("comment_password", window.passComment || "");
    setIsCommentModalOpen({
      id: fileId,
    });
  };

  const toggleTag = (fileId, tagId) => {
    if (albumDetail.is_locked) {
      setIsModalOpen(ACTION_ALBUM.LOCK);
      return;
    }

    // Tìm image bằng fileId
    const targetImage = images.find(img => img.id === fileId);
    if (!targetImage) return;

    mutateTag(
      {
        id: targetImage.id,
        tagId: tagId,
      },
      {
        onSuccess: (response) => {
          targetImage.tags = response.data.data;
          document
            .querySelector(".pswp__dynamic-caption .tag-wrap-" + fileId)
            ?.replaceChildren();
          targetImage.tags.forEach((tag) => {
            document
              .querySelector(".pswp__dynamic-caption .tag-wrap-" + fileId)
              ?.insertAdjacentElement(
                "beforeend",
                getTagDeleteButton(
                  checkedPath.includes(PATH_ALBUM.DASHBOARD),
                  fileId,
                  tag,
                  (fileId, id) => toggleTag(fileId, id)
                )
              );
          });
        },
      }
    );
  };

  const lightbox = useMemo(() => {

    const lb = new PhotoSwipeLightbox({
      gallery: document.getElementById("gallery"),
      children: Array.from(
        document.querySelectorAll(".pswp-gallery__item")
      ).sort((a,b) => {
        const aSort = (a as HTMLElement).dataset.fileSort;
        const bSort = (b as HTMLElement).dataset.fileSort;
        return parseInt(aSort) - parseInt(bSort);
      }) as HTMLElement[],
      loop: false,
      showHideAnimationType: "fade",
      mainClass: "text-xs lg:text-base",
      arrowPrev: true,
      arrowNext: true,
      imageClickAction: (releasePoint, e) => {
        if (window.clickImage) return likeF(window.pswp.currSlide.data.element.id, e);
        window.clickImage = true;
        setTimeout(() => {
          window.clickImage = false;
        }, 200);
      },
      bgClickAction: false,
      doubleTapAction: (releasePoint, e) => {
        likeF(window.pswp.currSlide.data.element.id, e);
      },
      zoom: false,
      pswpModule: () => import("photoswipe"),
    });

    !checkedPath.includes(PATH_ALBUM.SHOW) &&
      lb.on("uiRegister", function () {
        lb.pswp.ui.registerElement({
          name: "recommended-button",
          className: "pswp__recommended-button cursor-pointer",
          order: 9,
          html: "<div></div>",
          onInit: (el, pswp) => {
            pswp.on("change", () => {
              const currentFileId = pswp.currSlide.data.element.id;
              const currentImage = images.find(img => img.id === currentFileId);
              const isRecommended = currentImage?.is_recommended;
              el.innerHTML = getRecommendedUserButton(
                isRecommended,
                checkedPath.includes(PATH_ALBUM.DASHBOARD),
                t("albumDetail.album.isRecommended")
              );

              el.id = "recommend-sl-" + currentFileId;

              el.addEventListener("click", (e) => {
                checkedPath.includes(PATH_ALBUM.DASHBOARD) &&
                  recommendF(currentFileId, e);
              });
            });
          },
        });
      });
    lb.on("change", () => {
      const pswp = lb.pswp;
      const childrenCount =
        pswp.options.children instanceof Array
          ? pswp.options.children.length
          : 0;

      if (pswp.currIndex + 24 > childrenCount) {
        // Only fetch next page if not already fetching
        if (!window.isFetchingNextPage) {
          window.isFetchingNextPage = true;
          fetchNextPage().finally(() => {
            window.isFetchingNextPage = false;
          });
        }
      }
      const p = (pswp as any).currSlide.dynamicCaption.element as HTMLElement;

      if (!p) {
        return;
      }
      //Handle liked photo
      const currentFileId = pswp.currSlide.data.element.id;
      const btnLiked = p.querySelector("#like-sl-" + currentFileId);
      if (btnLiked) {
        btnLiked.addEventListener("click", (e) => {
          likeF(currentFileId, e);
        });
      }

      //Hanlde download photo
      const btnDownload = p.querySelector(".download") as HTMLAnchorElement;
      if (btnDownload) {
        btnDownload.onclick = (event: MouseEvent): void => {
          event.preventDefault();
          const fileUrl =
            new URL(btnDownload.href).origin +
            new URL(btnDownload.href).pathname;
          const fileName = btnDownload.getAttribute("data-file_name");
          const loaderIcon = btnDownload.querySelector(".loader-icon-file");
          const downloadIcon = btnDownload.querySelector(".download-icon-file");

          downloadIcon.classList.toggle("hidden");
          loaderIcon.classList.toggle("hidden");
          (event.target as HTMLAnchorElement).style.pointerEvents = "none";
          fetch(fileUrl)
            .then((response) => response.blob())
            .then(async (blob) => {
              if (!isIOS()) {
                const url = window.URL.createObjectURL(blob);
                saveAs(url, fileName);
                window.URL.revokeObjectURL(url);
              } else {
                const file = new File([blob], fileName, { type: blob.type });

                if (
                  navigator.canShare &&
                  navigator.canShare({ files: [file] })
                ) {
                  await navigator.share({
                    files: [file],
                    title: "Lưu ảnh",
                    text: "Nhấn để lưu ảnh vào Photos",
                  });
                } else {
                  alert("Trình duyệt không hỗ trợ chia sẻ ảnh.");
                }
              }
            })
            .catch((error) => {
              console.error("Error downloading file:", error);
            })
            .finally(() => {
              downloadIcon.classList.toggle("hidden");
              loaderIcon.classList.toggle("hidden");
              (event.target as HTMLAnchorElement).style.pointerEvents = "auto";
            });
        };
      }

      //Handle commented
      const editComment = p.querySelectorAll(".edit-comment");
      editComment &&
        editComment.forEach((item, i) => {
          item.addEventListener("click", () => {
            commentF(currentFileId);
          });
        });

      const addTag = p.querySelector(".pswp__dynamic-caption .add-tag");
      addTag &&
        addTag.addEventListener("click", () => {
          let tagModal = document.getElementById("dropdownTag");
          if (tagModal) {
            tagModal.remove();
          } else {
            const currentImage = images.find(img => img.id === currentFileId);
            const tagModal = getTagModal(
              albumDetail.tags,
              currentImage?.tags || [],
              (id) => toggleTag(currentFileId, id)
            );
            document
              .querySelector(".pswp__dynamic-caption #tag-" + currentFileId)
              .insertAdjacentElement("beforeend", tagModal);
          }
        });

      if (checkedPath.includes(PATH_ALBUM.ANO)) {
        const wrapTag = p.querySelector(".pswp__dynamic-caption .tag-wrap");
        wrapTag && addEventTag(wrapTag, (fileId, id) => toggleTag(fileId, id));
      }

      const changeThumbnail = p.querySelector(".change-thumbnail");
      changeThumbnail &&
        changeThumbnail.addEventListener("click", () => {
          const currentImage = images.find(img => img.id === currentFileId);
          mutateChangeThumbnail({
            id: albumDetail.id,
            thumbnail_url: currentImage?.url,
          }, {
            onSuccess: () => {
              setIsModalOpen(ACTION_ALBUM.CHANGE_THUMBNAIL);
            },
            onError: () => {
              setIsError("change-thumbnail");
            },
          });
        });
    });

    new PhotoSwipeDynamicCaption(lb, {
      mobileLayoutBreakpoint: 700,
      type: "below",
      mobileCaptionOverlapRatio: 1,
    });
    lb.addFilter("numItems", (number, dataSource) => {
      return albumDetail.file_count;
    });
    lb.init();

    window.lightbox = lb;

    return lb;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reorderedImages]);

  useEffect(() => {
    lightbox.options.children = Array.from(
      document.querySelectorAll(".pswp-gallery__item")
    ).sort((a,b) => {
      const aSort = (a as HTMLElement).dataset.fileSort;
      const bSort = (b as HTMLElement).dataset.fileSort;
      return parseInt(aSort) - parseInt(bSort);
    }) as HTMLElement[];

    lightbox.options.gallery = document.getElementById("gallery");

    lightbox.options.dataSource = lightbox.options.children;
    if (window.pswp?.options?.dataSource) {
      window.pswp.options.dataSource = lightbox.options.dataSource;
      window.pswp.options.children = lightbox.options.children;
    }

    lightbox.init();
    // return () => {
    //   console.log('destroy');
    //   lightbox.destroy();
    // };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reorderedImages]);

  useEffect(() => {
    if (!window.pswp) {
      return;
    }
    if (isCommentModalOpen) {
      window.pswp.events.removeAll();
    } else {
      window.pswp.updateSize();
      if (/iPhone|iPad|iPod/i.test(window.navigator.userAgent)) {
        setTimeout(() => {
          window.pswp.updateSize();
        }, 500);
      }
      window.pswp.setScrollOffset(0, window.pageYOffset);
      window.pswp.dispatch("bindEvents");
    }
  }, [isCommentModalOpen]);

  return (
    <div id="gallery">
        <ResponsiveMasonry columnsCountBreakPoints={columnsCountBreakPoints} className="my-masonry-grid">
          <Masonry>
            {reorderedImages.map((image, i) => {
              // Nếu đang sử dụng fallback (reorderedImages === images), sử dụng index trực tiếp
              let aspectRatio;
              let originalIndex = -1;
              if (reorderedImages === images) {
                aspectRatio = aspectRatios[i];
              } else {
                // Tìm index gốc của image trong mảng images ban đầu
                originalIndex = images.findIndex(img => img.id === image.id);
                aspectRatio = originalIndex !== -1 ? aspectRatios[originalIndex] : undefined;
              }

              return <Card key={image.id} image={image} i={originalIndex !== -1 ? originalIndex : i} checkedPath={checkedPath} tagShow={tagShow} setTagShow={setTagShow} setIsModalOpen={setIsModalOpen} setIsError={setIsError} mutateRecommend={mutateRecommend} mutateLiked={mutateLiked} albumDetail={albumDetail} isCommentPassword={isCommentPassword} isDownloadable={isDownloadable} toggleTag={toggleTag} handleImageLoad={handleImageLoad} aspectRatio={aspectRatio} />
            })}
          </Masonry>
        </ResponsiveMasonry>
      <Modal
        title={
          <div className="text-primary font-medium text-2xl">
            {t("albumDetail.album.titleComment")}
          </div>
        }
        open={!!isCommentModalOpen}
        onCancel={() => {
          setIsCommentModalOpen(null);
          form.resetFields();
        }}
        footer={null}
        className="photo-gallery"
        destroyOnClose
      >
        {errorPassComment && (
          <div className="text-sm text-primary mr-auto">{errorPassComment}</div>
        )}
        <div className="flex flex-col gap-5">
          <Form
            form={form}
            name="comment-photo-form"
            layout="vertical"
            onFinish={onFinish}
            colon={false}
          >
            <Form.Item
              name="comment"
              label={t("albumDetail.album.labelComment")}
            >
              <TextArea
                autoSize
                autoFocus
                onFocus={(e) => {
                  e.target.focus();
                }}
                maxLength={400}
              />
            </Form.Item>
            <Form.Item
              name="comment_password"
              className={`mb-0 relative ${passComment ? "hidden" : ""}`}
              label={t("albumDetail.album.commentPassword")}
              rules={[
                {
                  required: true,
                  message: (
                    <span className="text-xs absolute">
                      {t("formCreateAlbum.requirePass")}
                    </span>
                  ),
                },
                {
                  min: 6,
                  message: (
                    <span className="text-xs mb-1 block">
                      {t("formLogin.minPassword")}
                    </span>
                  ),
                },
              ]}
            >
              <Input type="password" />
            </Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              className={`submit-comment success transition-all flex gap-2 items-center w-fit ml-auto mt-4`}
              disabled={loadingComment}
              loading={loadingComment}
              onClick={() => {}}
            >
              <Save className="w-4" />
              {t("albumDetail.album.saveComment")}
            </Button>
          </Form>
        </div>
      </Modal>
      <MessageModal
        isVisible={isError === "like" || isError === "change-thumbnail"}
        onClose={() => {
          setIsError(undefined);
        }}
        title={isError === "like" ? t("albumDetail.album.limitLike") : t("albumDetail.album.errorChangeThumbnailTitle")}
        subTitle={isError === "like" ? t("albumDetail.album.limitLikeSub", { photo: maxFileLikes }) : t("albumDetail.album.errorChangeThumbnail")}
        type="error"
        btnText={t("albumDetail.album.btnClose")}
      />
      <MessageModal
        title={isModalOpen === ACTION_ALBUM.CHANGE_THUMBNAIL ? t("albumDetail.album.titleChangeThumbnail") : t("albumDetail.album.titleLock")}
        isVisible={!!isModalOpen}
        onClose={() => setIsModalOpen(null)}
        subTitle={isModalOpen === ACTION_ALBUM.CHANGE_THUMBNAIL ? t("albumDetail.album.subTitleChangeThumbnail") : t("albumDetail.album.subTitleLock")}
        type={isModalOpen === ACTION_ALBUM.CHANGE_THUMBNAIL ? "success" : "error"}
        btnText={t("albumDetail.album.btnClose")}
      />
    </div>
  );
};
export default AlbumGallery;
